<template>
    <div class="EvieChatConversationPanel">
        <div class="EvieChatConversationPanel__threadWrapper">
            <EvieChatThinking
                v-if="isLoading"
                class="EvieChatConversationPanel__loader"
                :visible="true"
            />
            <EvieChatThread
                v-else
                ref="thread"
                :message-ids="activeConversation ? activeConversation.messageIds : []"
                :messages-by-id="activeConversation ? activeConversation.messagesById : {}"
                :send-status="sendStatus"
                :streaming-message="streamingMessage"
                :spacer-height="inputAreaHeight"
                @retry="$emit('retry', $event)"
                @new-chat="$emit('new-chat')"
                @feedback="$emit('feedback', $event)"
                @scroll-state-change="isThreadAtBottom = $event"
            />
        </div>
        <div v-if="!isLoading && !isThreadAtBottom" class="EvieChatConversationPanel__jumpButtonWrapper">
            <EvieChatThreadJumpButton :loading="sendStatus === 'loading'" @click="scrollToBottom" />
        </div>
        <div ref="inputArea" class="EvieChatConversationPanel__inputArea">
            <div class="EvieChatConversationPanel__inputInner">
                <PromptInput
                    ref="promptInput"
                    :value="inputDraft"
                    :is-loading="sendStatus === 'loading' || isLoading"
                    :can-cancel="streamingMessage !== null"
                    @send="$emit('send', $event)"
                    @cancel="$emit('cancel')"
                    @input="$emit('draft-change', $event)"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.EvieChatConversationPanel {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow: hidden;

    &__threadWrapper {
        grid-column: 1;
        grid-row: 1 / -1;
        min-height: 0;
        overflow: hidden;
    }

    &__jumpButtonWrapper {
        grid-column: 1;
        grid-row: 1;
        align-self: end;
        z-index: 1;
        display: flex;
        justify-content: center;
        padding-bottom: 16px;
        pointer-events: none;
    }

    &__inputArea {
        grid-column: 1;
        grid-row: 2;
        z-index: 1;
    }

    &__inputInner {
        max-width: 720px;
        margin: 0 auto;
        background: var(--eidWhite);
        padding: 0 16px 8px;
    }

    &__loader {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { EvieConversation } from '@/modules/evie-ai/models/EvieConversation.model';

    import EvieChatThinking from '../../EvieChatThinking/EvieChatThinking.vue';
    import EvieChatThread from '../../EvieChatThread/EvieChatThread.vue';
    import EvieChatThreadJumpButton from '../../EvieChatThread/sub-components/EvieChatThreadJumpButton.vue';
    import PromptInput from '../../PromptInput/PromptInput.vue';

    export default defineComponent({
        name: 'EvieChatConversationPanel',
        components: { EvieChatThinking, EvieChatThread, EvieChatThreadJumpButton, PromptInput },
        props: {
            activeConversation: { type: Object as PropType<EvieConversation | null>, default: null },
            sendStatus: { type: String as PropType<'idle' | 'loading'>, required: true },
            inputDraft: { type: String, required: true },
            isLoading: { type: Boolean, default: false },
            streamingMessage: { type: String as PropType<string | null>, default: null },
        },
        emits: [ 'cancel', 'draft-change', 'feedback', 'new-chat', 'retry', 'send' ],
        data() {
            return {
                isThreadAtBottom: true as boolean,
                inputAreaHeight: 96 as number,
                resizeObserver: null as ResizeObserver | null,
            };
        },
        watch: {
            'activeConversation.messageIds': {
                handler() {
                    if (this.activeConversation && this.isThreadAtBottom) {
                        this.$nextTick(() => this.scrollToBottom());
                    }
                },
            },
            sendStatus(val: string) {
                if (val === 'loading') {
                    this.$nextTick(() => this.scrollToBottom());
                }
            },
            isLoading(newVal: boolean) {
                if (newVal) {
                    // Reset so the jump button doesn't flash when the new conversation loads.
                    this.isThreadAtBottom = true;
                } else {
                    // Covers both: initial load of /evie/:id and switching conversations.
                    // EvieChatThread is unmounted while isLoading=true (v-else), so it
                    // remounts here — scroll and focus after the DOM has updated.
                    this.$nextTick(() => {
                        this.scrollToBottom();
                        this.focusInput();
                    });
                }
            },
        },
        mounted() {
            const el = this.$refs.inputArea as HTMLElement | undefined;
            if (el) {
                this.resizeObserver = new ResizeObserver((entries) => {
                    const height = entries[0]?.contentRect.height ?? 96;
                    this.inputAreaHeight = Math.max(height, 96);
                });
                this.resizeObserver.observe(el);
            }
            // Focus when the panel mounts already loaded (e.g. navigating back to an
            // open conversation within the same session without a loading transition).
            if (!this.isLoading) {
                this.$nextTick(() => this.focusInput());
            }
        },
        beforeUnmount() {
            this.resizeObserver?.disconnect();
        },
        methods: {
            scrollToBottom() {
                (this.$refs.thread as any)?.scrollToBottom();
            },
            focusInput() {
                (this.$refs.promptInput as any)?.focus();
            },
        },
    });
</script>
